import DatePicker from "@/components/date-picker";
import { InputCurrency } from "@/components/input-currency/InputCurrency";
import { InputField } from "@/components/input-field/InputField";
import { CustomPopover } from "@/components/popover/CustomPopover";
import {
  AsyncAutocompleteSelect,
  Option,
} from "@/components/select/AsyncAutocompleteSelect";
import { SelectComponent } from "@/components/select/Select";
import { EntityTypes } from "@/constants/enums";
import { loadOptionsHandler } from "@/pages/requests/cash/schema/handler-functions";
import { getOptionsHandler } from "@/pages/requests/check/schema/handler-functions";
import {
  EntityStatusOption,
  getStatusByEntity,
} from "@/services/entity-status.service";

import { AppColors } from "@/styles/colors";
import {
  Badge,
  Box,
  Button,
  Field,
  Flex,
  Grid,
  GridItem,
  HStack,
  Text,
} from "@chakra-ui/react";
import { Dayjs } from "dayjs";
import { Filter, SearchIcon } from "lucide-react";
import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import { Control, FieldValues, UseFormHandleSubmit } from "react-hook-form";

interface Props {
  handleSubmit: UseFormHandleSubmit<FieldValues>;
  openFilters: boolean;
  setOpenFilters: Dispatch<SetStateAction<boolean>>;
  submitFilters: (event: FieldValues) => void;
  clearFilters: () => void;
  control: Control<FieldValues>;
  disableFilters?: boolean;
  loadingFilters?: boolean;
  filtersApplied?: boolean;
  disableClear?: boolean;
  loadingOptions?: boolean;
  maxDate?: Dayjs;
  scheduled?: boolean;
}

export const TransferRequestFilter = ({
  openFilters,
  disableFilters,
  loadingFilters,
  filtersApplied,
  disableClear,
  control,
  loadingOptions,
  maxDate,
  scheduled = false,
  submitFilters,
  handleSubmit,
  setOpenFilters,
  //handleSearch,
  clearFilters,
}: Props) => {
  const [isActive, setIsActive] = useState<boolean>(false);

  useEffect(() => {
    setIsActive(true);
  }, []);
  const [statusOptions, setStatusOptions] = useState<EntityStatusOption[]>([]);
  const [loadingStatusOptions, setLoadingStatusOptions] =
    useState<boolean>(false);

  const fetchStatusOptions = async () => {
    setLoadingStatusOptions(true);
    const response = await getStatusByEntity({
      Id: scheduled ? [EntityTypes.TransferenciaProgramada] : [EntityTypes.Transferencia, EntityTypes.TransferenciaDiferida]
    });

    setStatusOptions(response ?? []);
    setLoadingStatusOptions(false);
  };

  useEffect(() => {
    if (isActive) fetchStatusOptions();
  }, [isActive]);

  const getOptions = async (
    inputValue: string,
  ): Promise<
    {
      label: string;
      value: string;
      tipoCuenta: string;
      idTipoCuenta: number;
    }[]
  > => {
    return getOptionsHandler({
      inputValue,
    });
  };

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const [loadingAccounts, setLoadingAccounts] = useState<boolean>(false);

  const loadOptions = (inputValue: string): Promise<Option[]> => {
    return loadOptionsHandler({
      getOptions,
      inputValue,
      setLoadingPersons: setLoadingAccounts,
      timeoutRef,
    });
  };
  return (
    <Flex width="100%" paddingY="10px" justifyContent="flex-end">
      <HStack gap="10">
        <CustomPopover
          open={openFilters}
          onOpenChange={(e) => setOpenFilters(e.open)}
          triggerComponent={
            <Box position="relative" display="inline-block">
              <Button
                variant="outline"
                paddingX="12px"
                height="32px"
                disabled={disableFilters || loadingFilters}
              >
                <Filter />
                <Text fontWeight={600} fontSize="14px">
                  Filtrar
                </Text>
              </Button>
              {filtersApplied && (
                <Badge
                  position="absolute"
                  top="-4px"
                  right="-4px"
                  bg="red.500"
                  color="white"
                  fontSize="10px"
                  borderRadius="full"
                  px="2"
                  py="0.5"
                >
                  !
                </Badge>
              )}
            </Box>
          }
          bodyComponent={
            <Flex
              as="form"
              flexDir="column"
              padding="25px"
              gap="15px"
              onSubmit={handleSubmit(submitFilters)}
            >
              <Flex alignItems={"center"} justifyContent="space-between">
                <Text fontWeight={600} fontSize="20px">
                  Filtrar
                </Text>

                <Button
                  variant="plain"
                  color={AppColors.BLUE_TEXT}
                  fontWeight={600}
                  onClick={clearFilters}
                  disabled={loadingFilters || disableClear}
                  loading={loadingFilters}
                >
                  Limpiar filtros
                </Button>
              </Flex>
              <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                <GridItem>
                  <DatePicker
                    control={control}
                    labelText="Fecha"
                    name="request_date"
                    placeholder="Ingresar fecha"
                    maxDate={maxDate}
                  />
                </GridItem>
                <GridItem maxWidth="250px">
                  <Field.Root>
                    <Field.Label>
                      <Text fontWeight={600}>Cuenta origen</Text>
                    </Field.Label>
                    <AsyncAutocompleteSelect
                      control={control}
                      loadOptions={loadOptions}
                      name="origin_account"
                      placeholder="Buscar..."
                      isLoading={loadingAccounts}
                      disabled={
                        disableFilters || loadingFilters || loadingAccounts
                      }
                      closeMenuOnSelect
                      isClearable
                      triggerWithLength
                    />
                  </Field.Root>
                </GridItem>
                <GridItem maxWidth="250px">
                  <Field.Root>
                    <Field.Label>
                      <Text fontWeight={600}>Cuenta destino</Text>
                    </Field.Label>
                    <AsyncAutocompleteSelect
                      control={control}
                      loadOptions={loadOptions}
                      name="destiny_account"
                      placeholder="Buscar..."
                      isLoading={loadingAccounts}
                      disabled={
                        disableFilters || loadingFilters || loadingAccounts
                      }
                      closeMenuOnSelect
                      isClearable
                      triggerWithLength
                    />
                  </Field.Root>
                </GridItem>

                <GridItem>
                  <Field.Root>
                    <Field.Label>
                      <Text fontWeight={600}>Monto</Text>
                    </Field.Label>
                    <InputCurrency
                      name="request_amount"
                      placeholder="Ingresar monto"
                      control={control}
                      maxLength={20}
                      disabled={disableFilters || loadingFilters}
                    />
                  </Field.Root>
                </GridItem>
                <GridItem>
                  <InputField
                    inputName="requester"
                    labelText="Solicitante"
                    placeholder="Ingresar solicitante"
                    control={control}
                    startElement={
                      <SearchIcon
                        size="15px"
                        style={{ marginLeft: "12px", marginTop: "2px" }}
                      />
                    }
                    disabled={disableFilters || loadingFilters}
                  />
                </GridItem>
                <GridItem>
                  <SelectComponent
                    name="estados"
                    items={statusOptions.map((status) => ({
                      label: status.nombre,
                      value: status.ids.join("|"),
                    }))}
                    labelText="Estado"
                    placeholder="Seleccionar una opción"
                    control={control}
                    loading={loadingOptions || loadingStatusOptions}
                    multiple
                    menuMaxHeight="250px"
                  />
                </GridItem>
              </Grid>

              <Button
                bgColor={AppColors.GREEN_PRIMARY}
                type="submit"
                loading={loadingFilters}
                disabled={loadingFilters || disableFilters}
              >
                Aplicar filtros
              </Button>
            </Flex>
          }
        />
      </HStack>
    </Flex>
  );
};
